import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { soundchartsEndpoints } from "../../repositories/soundcharts";
import { pitchesApi } from "../../repositories/pitches";
import { formatDisplayDate, mmddyyToISO } from "../../DashboardPage/utils/dates";
import CSVDownload from "../../components/ui/CSVDownload";
import { countryCodes } from "./Data/dropdown";
import Select from "react-select"; // Add at the top

interface Pitch {
  id: number;
  userId: number;
  artistName: string;
  releaseTitle: string;
  releaseDate: string;
  configuration: string;
  releaseUPC: string;
  trackISRC: string;
  featuredArtists: string;
  featuredArtistURI: string;
  altTitles: string;
  shortDescription: string;
  longDescription: string;
  lyrics: string;
  songDescription: string;
  mainGenre: string;
  songMood: string;
  language: string;
  linkToListen: string;
  promoPhotos: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  AmazonInfo: any;
  SpotifyInfo: any;
  DeezerInfo: {
    id: number;
    priority: string;
    editorialTeam: string;
    subgenre: string;
    pitchId: number;
    createdAt: string;
    updatedAt: string;
  } | null;
  TidalInfo: {
    id: number;
    subgenre: string;
    artistPageLink: string;
    country: string;
    labelDistributor: string;
    socialHandles: string;
    pitchId: number;
    createdAt: string;
    updatedAt: string;
  } | null;
  AudiomackInfo: any;
  SoundCloudInfo: any;
  User: {
    id: number;
    fullName: string;
    email: string;
    labelId: number;
    Label: { id: number; name: string; type: string };
  };
  company: string;
}

interface Playlist {
  playlist: {
    uuid: string;
    name: string;
    imageUrl: string;
    platform: string;
    owner?: { name: string };
    type?: string;
    countryCode?: string;
  };
  peakPosition: number;
  position: number;
  entryDate: string;
  exitDate?: string;
  positionDate?: string;
  peakPositionDate?: string;
  isEditorial: boolean;
  playlistType: "editorial" | "algorithmic" | "algotorial";
}

const formatErrorMessage = (error: any): string => {
  if (typeof error === "string") return error;
  if (error?.response) {
    const { status, statusText, data } = error.response;
    switch (status) {
      case 400:
        return (
          data?.message || "Invalid request. Check your data and try again."
        );
      case 401:
        return "Unauthorized. Please log in again.";
      case 403:
        return "Permission denied.";
      case 404:
        return "Resource not found. Check the ID and try again.";
      case 429:
        return "Too many requests. Wait and try again.";
      case 500:
        return "Server error. Try again later.";
      case 502:
      case 503:
      case 504:
        return "Service unavailable. Try again in a few minutes.";
      default:
        return data?.message || `Failed with status ${status}: ${statusText}`;
    }
  }
  if (error?.code) {
    switch (error.code) {
      case "NETWORK_ERROR":
      case "ERR_NETWORK":
        return "Network failed. Check your internet.";
      case "TIMEOUT":
      case "ERR_TIMEOUT":
        return "Request timed out. Try again.";
      case "CANCELED":
      case "ERR_CANCELED":
        return "Request canceled.";
      default:
        return error.message || "Unexpected error. Try again.";
    }
  }
  return error?.message || "Unexpected error. Try again.";
};

const Analytics = () => {

  const { id } = useParams<{ id: string }>();
  const [pitch, setPitch] = useState<Pitch | null>(null);
  const [loading, setLoading] = useState(false);
  const [uuid, setUuid] = useState<string | null>(null);
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<
    "editorial" | "algorithmic" | "algotorial"
  >("editorial");
  const [activePlatform, setActivePlatform] = useState<string>("");
  const [availablePlatforms, setAvailablePlatforms] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [hideDuplicates, setHideDuplicates] = useState(true);
  const [currentOnly, setCurrentOnly] = useState<1 | 0>(1);
  const [totalAudience, setTotalAudience] = useState<number | null>(0);
  const [audienceByPlatform, setAudienceByPlatform] = useState<{ [key: string]: number | null }>({});
  const [isReleaseDateOlderThan30Days, setIsReleaseDateOlderThan30Days] = useState<boolean>(false);
  const [audienceLoading, setAudienceLoading] = useState(true);
  const [selectedCountries, setSelectedCountries] = useState<{ value: string; label: string }[]>([]);
  const platformDisplayNames: { [key: string]: string } = {
    spotify: "Spotify",
    amazon: "Amazon Music",
    deezer: "Deezer",
    tidal: "TIDAL",
    "apple-music": "Apple Music",
    youtube: "YouTube"
  };

  const getAudience = async (uuid: string, activePlatform: string, startDate: string, endDate: string) => {
    try {
      const res = await soundchartsEndpoints.getAudienceByUUIDAndPlatform(uuid, activePlatform, startDate, endDate);
      return res;
    } catch (error: any) {
      return error.response;

    }

  }

  function deduplicatePlaylists(playlists: Playlist[]) {
    // Only deduplicate within the same platform
    const map = new Map();
    for (const p of playlists) {
      const key = `${p.playlist.platform}||${p.playlist.name.toLowerCase()}`;
      // Keep the most recent (by entryDate)
      if (
        !map.has(key) ||
        new Date(p.entryDate) > new Date(map.get(key).entryDate)
      ) {
        map.set(key, p);
      }
    }
    return Array.from(map.values());
  }


  const fetchPlaylistsForCurrentTab = useCallback(
    async (
      songUuid: string,
      platform: string,
      type: "editorial" | "algorithmic" | "algotorial",
      offset: number = 0,
      currentOnly: 0 | 1,
      // countryCode:any
    ) => {
      try {
        const resPlaylists = await soundchartsEndpoints.getSongPlaylistsByType(
          songUuid,
          platform,
          type,
          {
            offset,
            limit: 50,
            sortBy: "entryDate",
            sortOrder: "desc",
            currentOnly,
            // countryCode
          }
        );
        const items = resPlaylists?.data?.items || [];

        const categorizedPlaylists = items.map((item: any) => ({
          ...item,
          isEditorial: type === "editorial",
          playlistType: type,
        }));

        const removeDuplicates = deduplicatePlaylists(categorizedPlaylists);

        const existingPlaylists = localStorage.getItem("playlists");

        if (existingPlaylists) {
          const parsedPlaylists = JSON.parse(existingPlaylists);
          if (parsedPlaylists[platform]) {
            parsedPlaylists[platform] = [
              ...parsedPlaylists[platform],
              ...removeDuplicates,
            ];
            // Remove duplicates
            parsedPlaylists[platform] = deduplicatePlaylists(
              parsedPlaylists[platform]
            );
          } else {
            parsedPlaylists[platform] = removeDuplicates;
          }
          localStorage.setItem("playlists", JSON.stringify(parsedPlaylists));
        } else {
          const newPlaylists = { [platform]: removeDuplicates };
          localStorage.setItem("playlists", JSON.stringify(newPlaylists));
        }

        setHasMore(items.length === 50);

        return categorizedPlaylists;
      } catch (error) {
        console.error(
          `❌ Error fetching ${type} playlists for ${platform}:`,
          error
        );
        setHasMore(false);
        return [];
      }
    },
    []
  );

  const fetchDataForCurrentTab = useCallback(async () => {
    if (!uuid || !activePlatform || !filterType) return;

    try {
      setLoading(true);
      setError(null);

      // Check cache first for this specific platform + type combination
      const storageKey = `playlists_${activePlatform}_${filterType}`;
      const cachedPlaylists = localStorage.getItem(storageKey);

      if (cachedPlaylists) {
        const parsedPlaylists = JSON.parse(cachedPlaylists);
        setPlaylists(parsedPlaylists);
        return;
      }
      const fetchedPlaylists = await fetchPlaylistsForCurrentTab(
        uuid,
        activePlatform,
        filterType, // Pass the current filter type to API
        0,
        currentOnly,
        // (selectedCountries.map(c => c.value)).join(',')
      );  

      setPlaylists(fetchedPlaylists);
      setOffset(0);
      setCurrentPage(1);
    } catch (error: any) {
      console.error("❌ Error fetching data:", error);
      setError(formatErrorMessage(error));
      setPlaylists([]);
    } finally {
      setLoading(false);
    }
  }, [uuid, activePlatform, filterType, fetchPlaylistsForCurrentTab,selectedCountries]);

  const loadMoreData = useCallback(async () => {
    if (!uuid || !activePlatform || !filterType || !hasMore) return;

    try {
      setLoading(true);
      const newOffset = offset + 50;

      const additionalPlaylists = await fetchPlaylistsForCurrentTab(
        uuid,
        activePlatform,
        filterType,
        newOffset,
        currentOnly,
        // (selectedCountries.map(c => c.value)).join(',')
      );

      const current = additionalPlaylists.filter((p: Playlist) => !p.exitDate);

      setPlaylists((prev) => [...prev, ...current]);
      setOffset(newOffset);
    } catch (error: any) {
      console.error("❌ Error loading more data:", error);
      setError(formatErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [
    uuid,
    activePlatform,
    filterType,
    offset,
    hasMore,
    fetchPlaylistsForCurrentTab,
  ]);

  const fetchInitialData = useCallback(async () => {
    if (!id) {
      setError("No pitch ID provided. Select a valid pitch.");
      setPitch(null);
      setPlaylists([]);
      return;
    }

    const pitchId = parseInt(id);
    if (isNaN(pitchId) || pitchId <= 0) {
      setError("Invalid pitch ID format. Provide a valid numeric ID.");
      setPitch(null);
      setPlaylists([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const pitchResponse = await pitchesApi.getOne(pitchId);
      if (!pitchResponse?.data?.data)
        throw new Error("Pitch not found or access denied.");

      const pitchData = pitchResponse.data.data;
      setPitch(pitchData);

      if (!pitchData.trackISRC) {
        console.warn("⚠️ No ISRC found for this pitch");
        setPlaylists([]);
        setAvailablePlatforms([]);
        return;
      }

      const songRes = await soundchartsEndpoints.getSongByISRC(
        pitchData.trackISRC
      );
      if (!songRes?.data?.object?.uuid) {
        console.warn(
          "⚠️ Song not found in Soundcharts for ISRC:",
          pitchData.trackISRC
        );
        setPlaylists([]);
        setAvailablePlatforms([]);
        return;
      }

      const fetchedUuid = songRes.data.object.uuid;
      setUuid(fetchedUuid);
      const audiencePlatforms = ["spotify", "youtube", "soundcloud", "audiomack"];
      let startDate: string;
      let endDate: string;
      const isReleaseDateOlderThan30Days = () => {
        if (!pitchData.releaseDate) return false;
        const today = mmddyyToISO(formatDisplayDate(new Date().toString()));
        let releaseDate = new Date(pitchData.releaseDate)
        let releaseDatePlus30Days = new Date(releaseDate);
        releaseDatePlus30Days.setDate(releaseDate.getDate() + 21);
        startDate = mmddyyToISO(formatDisplayDate(releaseDate.toString()));
        endDate = mmddyyToISO(formatDisplayDate(releaseDatePlus30Days.toString()));

        setIsReleaseDateOlderThan30Days(today >= endDate);
        return today >= endDate;
      }
      setAudienceLoading(true);
      { 
        isReleaseDateOlderThan30Days() && Promise.all(
          audiencePlatforms.map(platform =>
            getAudience(fetchedUuid, platform, startDate, endDate)
              .then(res => {
                if (res?.status === 404) {
                  return { platform, total: null };
                }
                let total = 0;
                if (res?.data?.items) {
                  res?.data.items.forEach((item: any) => {
                    item.plots?.forEach((plot: any) => {
                      total += plot.value;
                    });
                  });
                }
                return { platform, total }

              })
              .catch(err => {
                return { platform, total: null };

              })
          )).then(results => {
            // results is an array of { platform, total }
            const audienceObj: { [key: string]: number | null } = {};
            results.forEach(({ platform, total }) => {
              audienceObj[platform] = total;
            });
            setAudienceByPlatform(audienceObj);

            // Calculate total audience
            const allAudience = Object.values(audienceObj)
              .filter((total): total is number => typeof total === "number" && !isNaN(total))
              .reduce((sum, total) => sum + total, 0);
            setTotalAudience(allAudience);

          });
      }
      setTimeout(() => setAudienceLoading(false), 2000); // show loading for at least 2 seconds
      const platformMapping: { [key: string]: string } = {
        SpotifyInfo: "spotify",
        AmazonInfo: "amazon",
        DeezerInfo: "deezer",
        TidalInfo: "tidal",
        AppleInfo: "apple-music",
        YoutubeInfo: "youtube"
      };
      
      const platforms = Object.entries(platformMapping)
        .filter(([pitchKey]) => (pitchData as any)[pitchKey])
        .map(([, platformName]) => platformName);
      
      setAvailablePlatforms(platforms);
      if (platforms.length === 0) {
        setPlaylists([]);
        return;
      }

      setActivePlatform(platforms[0]);
    } catch (error: any) {
      console.error("❌ Error fetching initial data:", error);
      setError(formatErrorMessage(error));
      setPitch(null);
      setPlaylists([]);
      setAvailablePlatforms([]);
    } finally {
      setLoading(false);
    }
  }, [id]);
  useEffect(() => {
    const fetchData = async () => {
      if (!uuid || !activePlatform || !filterType) return;

      setLoading(true);
      try {
        const data = await fetchPlaylistsForCurrentTab(
          uuid,
          activePlatform,
          filterType,
          0,           // reset offset to 0
          currentOnly , // ✅ include this value
          // (selectedCountries.map(c => c.value)).join(',')          
        );
        setPlaylists(data);
        setOffset(0);
      } catch (error) {
        console.error("Error fetching playlists:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentOnly]); // ✅ watch for change


  useEffect(() => {
    if (activePlatform && filterType && uuid) {
      fetchDataForCurrentTab();
    }
  }, [activePlatform, filterType, fetchDataForCurrentTab]);

  useEffect(() => {
    fetchInitialData();

    return () => {
      localStorage.removeItem("playlists");
    };
  }, [fetchInitialData]);

  const handleRetry = () => {
    setOffset(0);
    setHasMore(true);
    console.log("Retrying fetch, reset offset to 0 and hasMore to true");
    setCurrentPage(1);
    if (activePlatform && filterType && uuid) {
      fetchDataForCurrentTab();
    } else {
      fetchInitialData();
    }
  };

  // Filtering and search logic
  let filteredPlaylists = playlists;
  if (activePlatform) {
    filteredPlaylists = filteredPlaylists.filter(
      (p) => p.playlist.platform === activePlatform
    );
  }

  if (searchTerm.trim()) {
    filteredPlaylists = filteredPlaylists.filter((p) =>
      p.playlist.name.toLowerCase().includes(searchTerm.trim().toLowerCase())
    );
  }

  // Deduplication
  if (hideDuplicates) {
    filteredPlaylists = deduplicatePlaylists(filteredPlaylists);
  }
  if (currentOnly === 0) {
    const todayStr = formatDisplayDate(new Date().toString());
    filteredPlaylists = filteredPlaylists.filter(
      (item) =>
        item.exitDate &&
        formatDisplayDate(item.exitDate) !== todayStr
    );
  }

  // Filter by selected countries
  if (selectedCountries.length > 0) {
    const selectedCountryCodes = selectedCountries.map(c => c.value);
    filteredPlaylists = filteredPlaylists.filter((item) =>
      item.playlist.countryCode && selectedCountryCodes.includes(item.playlist.countryCode)
    );
  }

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentPlaylists = filteredPlaylists.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(filteredPlaylists.length / itemsPerPage);

  // Determine if there's more data based on filtered results
  const shouldShowLoadMore = filteredPlaylists.length >= 10 && hasMore;

  const paginate = (pageNumber: number) => {
    if (pageNumber > totalPages && hasMore) {
      loadMoreData();
    }
    setCurrentPage(pageNumber);
  };

  const countryOptions = Object.entries(countryCodes).map(([code, name]) => ({
    value: code,
    label: name,
  }));
  console.log("selected Countries",selectedCountries.map(c => c.value).join(','));
  return (
    <div className="p-4 shadow-lg sm:p-6 bg-gray-50 rounded-xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800 sm:text-3xl">
          Analytics for {pitch?.releaseTitle || "Track"}
        </h1>
        <Link
          to="/past-submissions"
          className="px-4 py-2 text-sm font-bold text-white bg-teal-500 rounded hover:bg-teal-600"
        >
          Back to Past Submissions
        </Link>
      </div>

      {loading && !playlists.length ? (
        <div className="flex items-center justify-center p-8">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-teal-500 rounded-full animate-pulse"></div>
            <p className="text-gray-600">Loading analytics data...</p>
          </div>
        </div>
      ) : error ? (
        <div className="p-6 border border-red-200 rounded-lg bg-red-50">
          <div className="flex items-start space-x-3">
            <svg
              className="w-5 h-5 text-red-400"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <div>
              <h3 className="text-sm font-bold text-red-800">
                Error Loading Analytics
              </h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
              <button
                onClick={handleRetry}
                className="px-3 py-2 mt-4 text-sm font-bold text-red-700 bg-red-100 border border-red-300 rounded hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      ) : !pitch ? (
        <div className="p-6 text-center bg-gray-100 rounded-lg">
          <p className="text-gray-600">
            No pitch data found for the provided ID.
          </p>
        </div>
      ) : (
        <>

          <div className="mb-8">

            <h2 className="mb-4 text-xl font-semibold text-gray-800">
              Track Details
            </h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {pitch.artistName && (
                <div>
                  <p className="text-sm font-bold text-gray-700">Artist Name</p>
                  <p className="text-gray-800">{pitch.artistName}</p>
                </div>
              )}
              {pitch.releaseTitle && (
                <div>
                  <p className="text-sm font-bold text-gray-700">
                    Release Title
                  </p>
                  <p className="text-gray-800">{pitch.releaseTitle}</p>
                </div>
              )}
              {pitch.releaseDate && (
                <div>
                  <p className="text-sm font-bold text-gray-700">
                    Release Date
                  </p>
                  <p className="text-gray-800">
                    {formatDisplayDate(pitch.releaseDate)}
                  </p>
                </div>
              )}
              {pitch.configuration && (
                <div>
                  <p className="text-sm font-bold text-gray-700">
                    Configuration
                  </p>
                  <p className="text-gray-800">{pitch.configuration}</p>
                </div>
              )}
              {pitch.releaseUPC && (
                <div>
                  <p className="text-sm font-bold text-gray-700">UPC</p>
                  <p className="text-gray-800">{pitch.releaseUPC}</p>
                </div>
              )}
              {pitch.trackISRC && (
                <div>
                  <p className="text-sm font-bold text-gray-700">ISRC</p>
                  <p className="text-gray-800">{pitch.trackISRC}</p>
                </div>
              )}
              {pitch.featuredArtists && (
                <div>
                  <p className="text-sm font-bold text-gray-700">
                    Featured Artists
                  </p>
                  <p className="text-gray-800">
                    {pitch.featuredArtists || "—"}
                  </p>
                </div>
              )}
              {pitch.mainGenre && (
                <div>
                  <p className="text-sm font-bold text-gray-700">Main Genre</p>
                  <p className="text-gray-800">{pitch.mainGenre}</p>
                </div>
              )}
              {pitch.songMood && (
                <div>
                  <p className="text-sm font-bold text-gray-700">Song Mood</p>
                  <p className="text-gray-800">{pitch.songMood}</p>
                </div>
              )}
              {pitch.language && (
                <div>
                  <p className="text-sm font-bold text-gray-700">Language</p>
                  <p className="text-gray-800">{pitch.language}</p>
                </div>
              )}
              {pitch.linkToListen && (
                <div>
                  <p className="text-sm font-bold text-gray-700">
                    Link to Listen
                  </p>
                  <a
                    href={pitch.linkToListen}
                    className="text-teal-500 hover:underline break-all block"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {pitch.linkToListen}
                  </a>
                </div>
              )}
              {pitch.promoPhotos && (
                <div>
                  <p className="text-sm font-bold text-gray-700">
                    Promo Photos
                  </p>
                  <a
                    href={pitch.promoPhotos}
                    className="text-teal-500 hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {pitch.promoPhotos}
                  </a>
                </div>
              )}
            </div>
          </div>

          {!pitch.trackISRC ? (
            <div className="p-4 border border-yellow-200 rounded-lg bg-yellow-50">
              <p className="text-yellow-800">
                No ISRC found. Playlist analytics unavailable.
              </p>
            </div>
          ) : availablePlatforms.length === 0 ? (
            <div className="p-6 text-center bg-gray-100 rounded-lg">
              <p className="text-gray-600">
                No supported platforms found for this pitch.
              </p>
            </div>
          ) : (

            <> {isReleaseDateOlderThan30Days && (<div className="mb-8">
              <h2 className="mb-4 text-xl font-semibold text-gray-800">
                First Month Streams
              </h2>
              <div className="bg-white rounded-xl shadow border p-6">
                {audienceLoading ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="w-8 h-8 mb-4 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-gray-500 text-lg font-semibold">Loading audience data...</span>
                  </div>
                ) : (
                  <>
                    {/* Total Audience */}
                    <div className="flex flex-col items-center justify-center mb-6">
                      <span className="text-4xl font-extrabold text-teal-500">
                        {totalAudience?.toLocaleString() || 0}
                      </span>
                      <span className="text-lg font-bold text-gray-700 mt-1">streams</span>
                    </div>
                    <div className="border-t border-gray-200 my-4"></div>
                    {/* Platform Breakdown */}
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                      {["spotify", "youtube", "Soundcloud", "Audiomack"].map((platform) => (
                        <div
                          key={platform}
                          className="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-lg border"
                        >
                          <span className="font-semibold text-gray-700 mb-2">
                            {platformDisplayNames[platform] || platform}
                          </span>
                          <span className="font-bold text-teal-500 text-xl">
                            {audienceByPlatform[platform] == null ? "N/A" : (audienceByPlatform[platform]?.toLocaleString() || 0)}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 text-xs text-gray-500 text-center">
                      Audience numbers are aggregated for each platform during the first 30 days after release.
                    </div>
                  </>
                )}
              </div>
            </div>)

            }
              <div className="mb-6">
                <h2 className="mb-4 text-xl font-semibold text-gray-800">
                  Playlist Analytics by Platform
                </h2>
                <div className="flex flex-col gap-2 mb-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      placeholder="Search playlist names"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200"
                    />
                    <div className="flex items-center ml-4">
                      <label
                        htmlFor="dedup-toggle"
                        className="mr-2 text-sm font-medium text-gray-700"
                      >
                        Hide duplicate playlists
                      </label>
                      <input
                        id="dedup-toggle"
                        type="checkbox"
                        checked={hideDuplicates}
                        onChange={(e) => setHideDuplicates(e.target.checked)}
                        className="w-5 h-5 text-blue-600 form-checkbox"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                  {availablePlatforms.map((platform) => (
                    <button
                      key={platform}
                      onClick={() => setActivePlatform(platform)}
                      className={`px-4 py-2 text-sm font-bold rounded-lg transition-colors ${activePlatform === platform
                        ? "bg-teal-500 text-white"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                        }`}
                    >
                      {platformDisplayNames[platform] || platform}
                    </button>
                  ))}
                </div>

                <div className="flex mb-4 space-x-4">
                  {["editorial", "algorithmic", "algotorial"].map((type) => (
                    <label
                      key={type}
                      className="flex items-center py-2 space-x-2 cursor-pointer"
                    >
                      <input
                        type="radio"
                        value={type}
                        checked={filterType === type}
                        onChange={() =>
                          setFilterType(
                            type as "editorial" | "algorithmic" | "algotorial"
                          )
                        }
                        className="text-teal-500 form-radio"
                      />
                      <span
                        className={`text-sm ${filterType === type
                          ? "text-teal-600 font-semibold"
                          : "text-gray-700"
                          }`}
                      >
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="mb-4">
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Filter by Country
                </label>
                <Select
                  isMulti
                  options={countryOptions}
                  value={selectedCountries}
                  onChange={(selected:any) => setSelectedCountries(selected as any)}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  placeholder="Select countries..."
                />
              </div>

              <div className="mb-8">
                <div className="flex gap-4 mb-4">
                  <button
                    onClick={() => setCurrentOnly(1)}
                    className={`px-4 py-2 text-sm font-semibold rounded ${currentOnly === 1 ? "bg-teal-500 text-white" : "bg-gray-200 text-gray-800"
                      }`}
                  >
                    Current Data
                  </button>
                  <button
                    onClick={() => setCurrentOnly(0)}
                    className={`px-4 py-2 text-sm font-semibold rounded ${currentOnly === 0 ? "bg-teal-500 text-white" : "bg-gray-200 text-gray-800"
                      }`}
                  >
                    Historical Data
                  </button>
                </div>
                <div className="flex items-center justify-between w-full mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">
                    {currentOnly === 1 ? "Current" : "Historical"}{" "}
                    {filterType.charAt(0).toUpperCase() + filterType.slice(1)}{" "}
                    Playlists on {platformDisplayNames[activePlatform]}
                    {loading && (
                      <span className="ml-2 text-sm text-gray-500">
                        (Loading...)
                      </span>
                    )}
                  </h3>
                  <CSVDownload activePlatform={activePlatform} />
                </div>

                <div className="overflow-x-auto bg-white border border-gray-200 rounded-lg">
                  <table className="min-w-full">
                    <thead>
                      <tr className="bg-gray-100 border-b border-gray-200">
                        <th className="px-4 py-3 text-sm font-semibold text-left text-gray-700">
                          Playlist
                        </th>
                        <th className="px-4 py-3 text-sm font-semibold text-left text-gray-700">
                          Platform
                        </th>
                        <th className="px-4 py-3 text-sm font-semibold text-left text-gray-700">
                          Type
                        </th>
                        <th className="px-4 py-3 text-sm font-semibold text-left text-gray-700">
                          Peak Position
                        </th>
                        { currentOnly===1 &&<th className="px-4 py-3 text-sm font-semibold text-left text-gray-700">
                          Current Position
                        </th>}
                        {
                          currentOnly === 0 && (
                            <th className="px-4 py-3 text-sm font-semibold text-left text-gray-700">
                              Exit Date
                            </th>)
                        }
                        <th className="px-4 py-3 text-sm font-semibold text-left text-gray-700">
                          Entry Date
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPlaylists.length === 0 ? (
                        <tr>
                          <td
                            colSpan={7}
                            className="px-4 py-6 text-center text-gray-600"
                          >
                            No results found for your search and filter
                            criteria.
                          </td>
                        </tr>
                      ) : (
                        currentPlaylists.map((item, index) => (
                          <tr
                            key={`${item.playlist.uuid}-${item.playlistType}-${index}`}
                            className="border-b border-gray-100 hover:bg-gray-50"
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <img
                                  src={item.playlist.imageUrl}
                                  alt={item.playlist.name}
                                  className="object-cover bg-gray-200 rounded-md shadow-sm w-14 h-14"
                                  onError={(e) =>
                                  ((
                                    e.target as HTMLImageElement
                                  ).style.display = "none")
                                  }
                                />
                                <span
                                  className="max-w-xs text-sm text-gray-800 truncate"
                                  title={item.playlist.name}
                                >
                                  {item.playlist.name}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 capitalize">
                              {platformDisplayNames[item.playlist.platform] ||
                                item.playlist.platform}
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className={`px-2 py-1 text-xs font-bold rounded-full ${item.playlistType === "editorial"
                                  ? "text-black bg-teal-100"
                                  : item.playlistType === "algorithmic"
                                    ? "text-black bg-teal-100"
                                    : "text-black bg-teal-100"
                                  }`}
                              >
                                {item.playlistType === "editorial"
                                  ? "Editorial"
                                  : item.playlistType === "algorithmic"
                                    ? "Algorithmic"
                                    : "Algotorial"}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-800">
                              {item.peakPosition}
                            </td>
                            {currentOnly===1 &&<td className="px-4 py-3 text-sm text-gray-800">
                              {item.position || "—"}
                            </td>}
                            {
                              currentOnly === 0 && (
                                <td className="px-4 py-3 text-sm text-gray-800">
                                  {item.exitDate
                                    ? formatDisplayDate(item.exitDate)
                                    : "—"}
                                </td>)}

                            <td className="px-4 py-3 text-sm text-gray-800">
                              {item.entryDate
                                ? formatDisplayDate(item.entryDate)
                                : "—"}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="flex justify-center mt-4 space-x-2">
                  <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm font-bold text-gray-700 bg-gray-200 rounded disabled:opacity-50 hover:bg-gray-300"
                  >
                    Previous
                  </button>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => (
                    <button
                      key={i + 1}
                      onClick={() => paginate(i + 1)}
                      className={`px-3 py-1 text-sm font-bold rounded ${currentPage === i + 1
                        ? "bg-teal-500 text-white"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                        }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages && !shouldShowLoadMore}
                    className="px-3 py-1 text-sm font-bold text-gray-700 bg-gray-200 rounded disabled:opacity-50 hover:bg-gray-300"
                  >
                    Next
                  </button>
                  
                    <button
                      onClick={loadMoreData}
                      disabled={loading || !shouldShowLoadMore}
                      className="px-3 py-1 text-sm font-bold text-blue-700 bg-blue-100 rounded hover:bg-blue-200 disabled:opacity-50"
                    >
                      Load More
                    </button>
                  
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default Analytics;